import React, { useEffect, useState } from "react";
import axios from "axios";
import { useCultivationCompanyContext } from "../hooks/useCultivationCompanyContext";
import { useCompanyContext } from "../hooks/useCompanyContext";

const steps = [
  "Introductie",
  "Ras evaluaties",
  "Algemene ordervragen",
  "Levering",
  "Communicatie",
  "Algemene indruk",
];

const params = new URLSearchParams(window.location.search);
const orderNumber = params.get("order") || "DEMO-ORDER-001";

const boolToRadio = (value) => {
  if (value === true) return "ja";
  if (value === false) return "nee";
  return "";
};

const radioToBool = (value) => {
  if (value === "ja") return true;
  if (value === "nee") return false;
  return null;
};

const numberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;

  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const createRasData = (rassen = []) =>
  rassen.map((ras) => ({
    ras: ras.ras,
    quantity: ras.quantity || 0,
    description: ras.description || "",

    uniformiteit: "",
    trosOntwikkeling: "",
    trosOntwikkelingToelichting: "",

    dagenTotBloei: "",
    bladerenTussenTros: "",
    overigeOpmerking: "",

    witteVlieg: "",
    rupsTuta: "",
    trips: "",
    luis: "",
    andersZiekte: "",
    andersZiekteOpmerking: "",
  }));

const Evaluatie = () => {
  const { cultivationCompany } = useCultivationCompanyContext();
  const { t } = useCompanyContext();

  const tr = (key, fallback) => t?.[key] || fallback;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");

  const [evaluationMeta, setEvaluationMeta] = useState({
    customerId: null,
    evaluator: "PRODDB",
  });

  const [step, setStep] = useState(0);
  const [rasIndex, setRasIndex] = useState(0);
  const [rasSubStep, setRasSubStep] = useState(0);

  const [formData, setFormData] = useState({
    rassen: [],

    kwaliteitStok: "",
    kwaliteitClip: "",
    goedGesorteerd: "",
    karrenSchoon: "",
    opTijd: "",
    proevenAfwijkingenDuidelijk: "",
    plantenBeschadigd: "",

    verkoop: "",
    teeltManager: "",
    transport: "",

    algemeneIndruk: "",
  });

  const currentRas = formData.rassen[rasIndex] || null;

  const getAxiosConfig = () => {
    if (!cultivationCompany?.name) return {};

    return {
      headers: {
        "X-Cultivation-Company": cultivationCompany.name,
      },
    };
  };

  const handleGeneralChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateCurrentRas = (changes) => {
    setFormData((prev) => {
      const updatedRassen = prev.rassen.map((ras, index) =>
        index === rasIndex ? { ...ras, ...changes } : ras
      );

      return {
        ...prev,
        rassen: updatedRassen,
      };
    });
  };

  const handleRasChange = (field, value) => {
    updateCurrentRas({ [field]: value });
  };

  const mapEvaluationToFormData = (evaluation, availableRassen = []) => {
    const baseRassen = availableRassen.length
      ? availableRassen
      : (evaluation.rassen || []).map((ras) => ({
          ras: ras.ras,
          quantity: 0,
          description: "",
        }));

    const loadedRassen = baseRassen.map((availableRas) => {
      const savedRas = evaluation.rassen?.find(
        (item) => item.ras === availableRas.ras
      );

      const plantVerwachtingen = savedRas?.plantVerwachtingen || {};
      const ziektesPlagen = savedRas?.ziektesPlagen || {};

      return {
        ras: availableRas.ras,
        quantity: availableRas.quantity || 0,
        description: availableRas.description || "",

        uniformiteit: plantVerwachtingen.uniformiteit || "",
        trosOntwikkeling: plantVerwachtingen.trosOntwikkeling || "",
        trosOntwikkelingToelichting:
          plantVerwachtingen.trosOntwikkelingToelichting || "",

        dagenTotBloei: plantVerwachtingen.dagenTotBloei ?? "",
        bladerenTussenTros: plantVerwachtingen.bladerenTussenTros ?? "",
        overigeOpmerking: plantVerwachtingen.overigeOpmerking || "",

        witteVlieg: boolToRadio(ziektesPlagen.witteVlieg),
        rupsTuta: boolToRadio(ziektesPlagen.rupsen),
        trips: boolToRadio(ziektesPlagen.trips),
        luis: boolToRadio(ziektesPlagen.luis),
        andersZiekte: boolToRadio(ziektesPlagen.anders),
        andersZiekteOpmerking: ziektesPlagen.andersOpmerking || "",
      };
    });

    return {
      rassen: loadedRassen,

      kwaliteitStok: evaluation.levering?.kwaliteitStok || "",
      kwaliteitClip: evaluation.levering?.kwaliteitClip || "",
      goedGesorteerd: boolToRadio(evaluation.levering?.goedGesorteerd),
      karrenSchoon: boolToRadio(evaluation.levering?.karrenSchoon),
      opTijd: boolToRadio(evaluation.levering?.opTijd),
      proevenAfwijkingenDuidelijk: boolToRadio(
        evaluation.levering?.proevenAfwijkingenDuidelijk
      ),
      plantenBeschadigd: boolToRadio(evaluation.levering?.plantenBeschadigd),

      verkoop: evaluation.communicatie?.verkoop || "",
      teeltManager: evaluation.communicatie?.teeltManager || "",
      transport: evaluation.communicatie?.transport || "",

      algemeneIndruk: evaluation.algemeneIndruk ?? "",
    };
  };

  const buildPayload = () => ({
    orderNumber,
    customerId: evaluationMeta.customerId,
    evaluator: evaluationMeta.evaluator || "PRODDB",
    evaluationDate: new Date().toISOString(),

    rassen: formData.rassen.map((ras) => ({
      ras: ras.ras,

      plantVerwachtingen: {
        uniformiteit: ras.uniformiteit || null,
        trosOntwikkeling: ras.trosOntwikkeling || null,
        trosOntwikkelingToelichting:
          ras.trosOntwikkeling === "niet goed"
            ? ras.trosOntwikkelingToelichting || null
            : null,
        dagenTotBloei: numberOrNull(ras.dagenTotBloei),
        bladerenTussenTros: numberOrNull(ras.bladerenTussenTros),
        overigeOpmerking: ras.overigeOpmerking || null,
      },

      ziektesPlagen: {
        witteVlieg: radioToBool(ras.witteVlieg),
        rupsen: radioToBool(ras.rupsTuta),
        trips: radioToBool(ras.trips),
        luis: radioToBool(ras.luis),
        anders: radioToBool(ras.andersZiekte),
        andersOpmerking:
          ras.andersZiekte === "ja" ? ras.andersZiekteOpmerking || null : null,
      },
    })),

    levering: {
      kwaliteitStok: formData.kwaliteitStok || null,
      kwaliteitClip: formData.kwaliteitClip || null,
      goedGesorteerd: radioToBool(formData.goedGesorteerd),
      karrenSchoon: radioToBool(formData.karrenSchoon),
      opTijd: radioToBool(formData.opTijd),
      proevenAfwijkingenDuidelijk: radioToBool(
        formData.proevenAfwijkingenDuidelijk
      ),
      plantenBeschadigd: radioToBool(formData.plantenBeschadigd),
    },

    communicatie: {
      verkoop: formData.verkoop || null,
      teeltManager: formData.teeltManager || null,
      transport: formData.transport || null,
    },

    algemeneIndruk: numberOrNull(formData.algemeneIndruk),
  });

  const calculateProgress = ({
    nextStepValue = step,
    nextRasIndex = rasIndex,
    nextRasSubStep = rasSubStep,
  } = {}) => {
    const totalRasSubSteps = formData.rassen.length * 3;
    const totalUnits = 1 + totalRasSubSteps + 4;

    let completedUnits = 0;

    if (nextStepValue === 0) {
      completedUnits = 0;
    }

    if (nextStepValue === 1) {
      completedUnits = 1 + nextRasIndex * 3 + nextRasSubStep;
    }

    if (nextStepValue === 2) {
      completedUnits = 1 + totalRasSubSteps;
    }

    if (nextStepValue === 3) {
      completedUnits = 1 + totalRasSubSteps + 1;
    }

    if (nextStepValue === 4) {
      completedUnits = 1 + totalRasSubSteps + 2;
    }

    if (nextStepValue === 5) {
      completedUnits = 1 + totalRasSubSteps + 3;
    }

    if (totalUnits <= 1) return 0;

    return Math.min(Math.round((completedUnits / totalUnits) * 100), 99);
  };

  const saveProgress = async ({
    nextStepValue = step,
    nextRasIndex = rasIndex,
    nextRasSubStep = rasSubStep,
  } = {}) => {
    try {
      setSaving(true);
      setError("");

      const calculatedProgress = calculateProgress({
        nextStepValue,
        nextRasIndex,
        nextRasSubStep,
      });

      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/evaluations/${orderNumber}`,
        {
          ...buildPayload(),
          progress: calculatedProgress,
        },
        getAxiosConfig()
      );
    } catch (error) {
      if (error.response?.status === 409) {
        setIsCompleted(true);
        setError(
          tr(
            "evaluationErrorAlreadyCompleted",
            "Deze evaluatie is afgerond en kan niet meer worden aangepast."
          )
        );
        return;
      }

      setError(
        tr("evaluationErrorSaveFailed", "Opslaan van de evaluatie is mislukt.")
      );
      throw error;
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const fetchEvaluation = async () => {
      try {
        setLoading(true);
        setError("");
        setLoadError("");

        const [evaluationResponse, rassenResponse] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_API_URL}/api/evaluations/${orderNumber}`,
            getAxiosConfig()
          ),
          axios.get(
            `${process.env.REACT_APP_API_URL}/api/evaluations/${orderNumber}/rassen`,
            getAxiosConfig()
          ),
        ]);

        const evaluation = evaluationResponse.data;
        const fetchedRassen = rassenResponse.data || [];

        if (!fetchedRassen.length) {
          setLoadError(
            t?.evaluationErrorNoVarieties ||
              "Er zijn geen rassen gevonden voor deze order."
          );
          return;
        }

        setEvaluationMeta({
          customerId: evaluation.customerId || null,
          evaluator: evaluation.evaluator || "PRODDB",
        });

        setFormData(mapEvaluationToFormData(evaluation, fetchedRassen));
        setIsCompleted(evaluation.status === "completed");
      } catch (error) {
        if (error.response?.status === 403) {
          setLoadError(
            t?.evaluationErrorNotAllowed ||
              "Voor deze order is nog geen evaluatie toegestaan."
          );
          return;
        }

        setLoadError(
          error.response?.data?.message ||
            t?.evaluationErrorLoadFailed ||
            "Evaluatie kon niet worden geladen."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchEvaluation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber, cultivationCompany?.name]);

  const nextStep = async () => {
    const next = Math.min(step + 1, steps.length - 1);

    await saveProgress({
      nextStepValue: next,
    });

    setStep(next);
  };

  const prevStep = () => setStep((prev) => Math.max(prev - 1, 0));

  const nextRasStep = async () => {
    if (rasSubStep < 2) {
      const nextSubStep = rasSubStep + 1;

      await saveProgress({
        nextStepValue: step,
        nextRasIndex: rasIndex,
        nextRasSubStep: nextSubStep,
      });

      setRasSubStep(nextSubStep);
      return;
    }

    if (rasIndex < formData.rassen.length - 1) {
      const nextIndex = rasIndex + 1;

      await saveProgress({
        nextStepValue: step,
        nextRasIndex: nextIndex,
        nextRasSubStep: 0,
      });

      setRasIndex(nextIndex);
      setRasSubStep(0);
      return;
    }

    const next = step + 1;

    await saveProgress({
      nextStepValue: next,
      nextRasIndex: rasIndex,
      nextRasSubStep: rasSubStep,
    });

    setStep(next);
  };

  const prevRasStep = () => {
    if (rasSubStep > 0) {
      setRasSubStep((prev) => prev - 1);
      return;
    }

    if (rasIndex > 0) {
      setRasIndex((prev) => prev - 1);
      setRasSubStep(2);
      return;
    }

    prevStep();
  };

  const startEvaluation = async () => {
    await saveProgress({
      nextStepValue: 1,
      nextRasIndex: 0,
      nextRasSubStep: 0,
    });

    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError("");

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/evaluations/${orderNumber}/complete`,
        {
          ...buildPayload(),
          progress: 100,
        },
        getAxiosConfig()
      );

      setSubmitted(true);
      setIsCompleted(true);
    } catch (error) {
      if (error.response?.status === 409) {
        setIsCompleted(true);
        setError(
          tr(
            "evaluationErrorAlreadyCompleted",
            "Deze evaluatie is al afgerond en kan niet meer worden aangepast."
          )
        );
        return;
      }

      setError(
        tr(
          "evaluationErrorCompleteFailed",
          "Afronden van de evaluatie is mislukt."
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const progress = isCompleted
    ? 100
    : calculateProgress({
        nextStepValue: step,
        nextRasIndex: rasIndex,
        nextRasSubStep: rasSubStep,
      });

  if (loading) {
    return (
      <div className="evaluation-container">
        <h1 className="pageheading">
          {tr("evaluationLoadingTitle", "Evaluatie laden...")}
        </h1>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="evaluation-container">
        <h1 className="pageheading">
          {tr("evaluationUnavailableTitle", "Evaluatie niet beschikbaar")}
        </h1>
        <p>{loadError}</p>
      </div>
    );
  }

  if (isCompleted && !submitted) {
    return (
      <div className="evaluation-container">
        <h1 className="pageheading">
          {tr("evaluationCompletedTitle", "Evaluatie afgerond")}
        </h1>
        <p>
          {tr(
            "evaluationCompletedText",
            "Deze evaluatie is al volledig ingevuld en kan niet meer worden aangepast."
          )}
        </p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="evaluation-container">
        <h1 className="pageheading">
          {tr("evaluationThankYouTitle", "Bedankt voor uw evaluatie!")}
        </h1>
        <p>
          {tr(
            "evaluationThankYouText",
            "Uw feedback is succesvol verzonden."
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="evaluation-container">
      <h1 className="pageheading">
        {tr("evaluationTitle", "Order Evaluatie")} {orderNumber}
      </h1>

      <div className="progress-bar">
        <div className="progress" style={{ width: `${progress}%` }} />
      </div>

      <div className="evaluation-box">
        <form onSubmit={handleSubmit} className="evaluation-form">
          {error && <p className="error-message">{error}</p>}

          {step === 0 && (
            <div className="landing-step">
              <h2>{tr("evaluationWelcomeTitle", "Welkom bij de evaluatie")}</h2>
              <p>
                {tr("evaluationIntroOrderText", "Dit is de evaluatie van")}{" "}
                <strong>
                  {tr("order", "Order")} {orderNumber}
                </strong>
                .
                <br />
                {tr("evaluationIntroVarietyCountText", "Deze order bevat")}{" "}
                <strong>{formData.rassen.length}</strong>{" "}
                {tr("evaluationVarieties", "rassen")}.
              </p>

              <ul className="ras-list">
                {formData.rassen.map((ras) => (
                  <li key={ras.ras}>
                    {ras.ras}
                    {ras.description ? ` - ${ras.description}` : ""} —{" "}
                    {ras.quantity.toLocaleString("nl-NL")}{" "}
                    {tr("pieces", "stuks")}
                  </li>
                ))}
              </ul>

              <button
                type="button"
                className="submit-button"
                onClick={startEvaluation}
                disabled={saving}
              >
                {saving
                  ? tr("evaluationSavingButton", "Opslaan...")
                  : tr("evaluationStartButton", "Start evaluatie")}
              </button>
            </div>
          )}

          {step === 1 && currentRas && (
            <>
              <div className="ras-header">
                <span>
                  {tr("evaluationVariety", "Ras")} {rasIndex + 1}{" "}
                  {tr("of", "van")} {formData.rassen.length}
                </span>
                <strong>
                  {currentRas.ras}
                  {currentRas.description ? ` - ${currentRas.description}` : ""}{" "}
                  — {currentRas.quantity.toLocaleString("nl-NL")}{" "}
                  {tr("pieces", "stuks")}
                </strong>
              </div>

              {rasSubStep === 0 && (
                <>
                  <h2>{tr("evaluationVarietyInfoTitle", "Ras informatie")}</h2>

                  <label>{tr("evaluationVarietyLabel", "Ras")}:</label>
                  <p>{currentRas.ras}</p>

                  <label>{tr("evaluationQuantityLabel", "Hoeveelheid")}:</label>
                  <p>
                    {currentRas.quantity.toLocaleString("nl-NL")}{" "}
                    {tr("pieces", "stuks")}
                  </p>
                </>
              )}

              {rasSubStep === 1 && (
                <>
                  <h2>
                    {tr("evaluationPlantExpectationsTitle", "Plant verwachtingen")}
                  </h2>

                  <table className="radio-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>{tr("evaluationOptionEqual", "Gelijk")}</th>
                        <th>{tr("evaluationOptionUnequal", "Ongelijk")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          {tr(
                            "evaluationQuestionUniformity",
                            "Uniformiteit plant"
                          )}
                        </td>
                        {["gelijk", "ongelijk"].map((value) => (
                          <td key={value}>
                            <input
                              type="radio"
                              name={`uniformiteit-${rasIndex}`}
                              value={value}
                              checked={currentRas.uniformiteit === value}
                              onChange={(e) =>
                                handleRasChange("uniformiteit", e.target.value)
                              }
                            />
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>

                  <table className="radio-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>{tr("evaluationOptionGood", "Goed")}</th>
                        <th>{tr("evaluationOptionNotGood", "Niet goed")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          {tr(
                            "evaluationQuestionTrussDevelopment",
                            "Tros ontwikkeling"
                          )}
                        </td>
                        {["goed", "niet goed"].map((value) => (
                          <td key={value}>
                            <input
                              type="radio"
                              name={`trosOntwikkeling-${rasIndex}`}
                              value={value}
                              checked={currentRas.trosOntwikkeling === value}
                              onChange={(e) => {
                                const selectedValue = e.target.value;

                                updateCurrentRas({
                                  trosOntwikkeling: selectedValue,
                                  trosOntwikkelingToelichting:
                                    selectedValue === "goed"
                                      ? ""
                                      : currentRas.trosOntwikkelingToelichting,
                                });
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>

                  {currentRas.trosOntwikkeling === "niet goed" && (
                    <>
                      <label>
                        {tr(
                          "evaluationTrussDevelopmentExplanationLabel",
                          "Toelichting tros ontwikkeling"
                        )}
                      </label>
                      <select
                        className="input-field"
                        value={currentRas.trosOntwikkelingToelichting}
                        onChange={(e) =>
                          handleRasChange(
                            "trosOntwikkelingToelichting",
                            e.target.value
                          )
                        }
                      >
                        <option value="">
                          {tr("evaluationSelectPlaceholder", "Maak een keuze")}
                        </option>
                        <option value="bonken">
                          {tr("evaluationOptionBonken", "Bonken")}
                        </option>
                        <option value="splijttros">
                          {tr("evaluationOptionSplitTruss", "Splijttros")}
                        </option>
                        <option value="blaadje op tros">
                          {tr(
                            "evaluationOptionLeafOnTruss",
                            "Blaadje op tros"
                          )}
                        </option>
                      </select>
                    </>
                  )}

                  <label>
                    {tr(
                      "evaluationQuestionDaysToFlowering",
                      "Aantal dagen na levering bloei"
                    )}
                  </label>
                  <input
                    type="number"
                    className="input-field"
                    value={currentRas.dagenTotBloei}
                    onChange={(e) =>
                      handleRasChange("dagenTotBloei", e.target.value)
                    }
                  />

                  <label>
                    {tr(
                      "evaluationQuestionLeavesBetweenTrusses",
                      "Aantal bladeren tussen tros 1 en 2"
                    )}
                  </label>
                  <input
                    type="number"
                    className="input-field"
                    value={currentRas.bladerenTussenTros}
                    onChange={(e) =>
                      handleRasChange("bladerenTussenTros", e.target.value)
                    }
                  />

                  <label>
                    {tr(
                      "evaluationQuestionOtherRemarks",
                      "Overige opmerkingen"
                    )}
                  </label>
                  <select
                    className="input-field"
                    value={currentRas.overigeOpmerking}
                    onChange={(e) =>
                      handleRasChange("overigeOpmerking", e.target.value)
                    }
                  >
                    <option value="">
                      {tr(
                        "evaluationOptionNoneNotApplicable",
                        "Geen / niet van toepassing"
                      )}
                    </option>
                    <option value="koploos">
                      {tr("evaluationOptionHeadless", "Koploos")}
                    </option>
                    <option value="kroeskop">
                      {tr("evaluationOptionCurlyHead", "Kroeskop")}
                    </option>
                    <option value="aantal scheuten wijkt af">
                      {tr(
                        "evaluationOptionShootCountDeviation",
                        "Aantal scheuten wijkt af"
                      )}
                    </option>
                    <option value="pot te droog">
                      {tr("evaluationOptionPotTooDry", "Pot te droog")}
                    </option>
                  </select>
                </>
              )}

              {rasSubStep === 2 && (
                <>
                  <h2>{tr("evaluationDiseasesPestsTitle", "Ziektes / plagen")}</h2>

                  <table className="radio-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>{tr("yes", "Ja")}</th>
                        <th>{tr("no", "Nee")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        {
                          key: "witteVlieg",
                          label: tr(
                            "evaluationQuestionWhitefly",
                            "Witte vlieg"
                          ),
                        },
                        {
                          key: "rupsTuta",
                          label: tr("evaluationQuestionCaterpillarTuta", "Rups / tuta"),
                        },
                        {
                          key: "trips",
                          label: tr("evaluationQuestionThrips", "Trips"),
                        },
                        {
                          key: "luis",
                          label: tr("evaluationQuestionAphids", "Luis"),
                        },
                        {
                          key: "andersZiekte",
                          label: tr("evaluationQuestionOther", "Anders"),
                        },
                      ].map((item) => (
                        <tr key={item.key}>
                          <td>{item.label}</td>
                          {["ja", "nee"].map((value) => (
                            <td key={value}>
                              <input
                                type="radio"
                                name={`${item.key}-${rasIndex}`}
                                value={value}
                                checked={currentRas[item.key] === value}
                                onChange={(e) => {
                                  const selectedValue = e.target.value;

                                  if (item.key === "andersZiekte") {
                                    updateCurrentRas({
                                      andersZiekte: selectedValue,
                                      andersZiekteOpmerking:
                                        selectedValue === "nee"
                                          ? ""
                                          : currentRas.andersZiekteOpmerking,
                                    });
                                  } else {
                                    handleRasChange(item.key, selectedValue);
                                  }
                                }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {currentRas.andersZiekte === "ja" && (
                    <>
                      <label>
                        {tr(
                          "evaluationOtherDiseaseRemarkLabel",
                          "Opmerking bij anders"
                        )}
                      </label>
                      <textarea
                        className="textarea-field"
                        value={currentRas.andersZiekteOpmerking}
                        onChange={(e) =>
                          handleRasChange(
                            "andersZiekteOpmerking",
                            e.target.value
                          )
                        }
                        placeholder={tr(
                          "evaluationOtherDiseaseRemarkPlaceholder",
                          "Beschrijf welke ziekte of plaag is waargenomen..."
                        )}
                      />
                    </>
                  )}
                </>
              )}
            </>
          )}

          {step === 2 && (
            <div className="transition-step">
              <h2>
                {tr(
                  "evaluationGeneralDeliveryQuestionsTitle",
                  "Algemene vragen over de levering"
                )}
              </h2>
              <p>
                {tr(
                  "evaluationVarietyQuestionsCompletedText",
                  "De ras-specifieke vragen zijn ingevuld."
                )}
              </p>
              <p>
                {tr(
                  "evaluationGeneralDeliveryQuestionsTextBeforeOrder",
                  "De volgende vragen gaan over de volledige levering van"
                )}{" "}
                <strong>
                  {tr("order", "order")} {orderNumber}
                </strong>{" "}
                {tr(
                  "evaluationGeneralDeliveryQuestionsTextAfterOrder",
                  "en gelden dus niet per ras, maar voor de order als geheel."
                )}
              </p>
            </div>
          )}

          {step === 3 && (
            <>
              <h2>{tr("evaluationDeliveryTitle", "Levering")}</h2>

              <table className="radio-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>{tr("evaluationOptionGood", "Goed")}</th>
                    <th>{tr("evaluationOptionModerate", "Matig")}</th>
                    <th>{tr("evaluationOptionBad", "Slecht")}</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      key: "kwaliteitStok",
                      label: tr(
                        "evaluationQuestionStakePlacement",
                        "Stok(-ken) op juiste plek gestoken"
                      ),
                    },
                    {
                      key: "kwaliteitClip",
                      label: tr(
                        "evaluationQuestionElasticClipHeight",
                        "Stiek/ clip op juiste hoogte aangebracht"
                      ),
                    },
                  ].map((item) => (
                    <tr key={item.key}>
                      <td>{item.label}</td>
                      {["goed", "matig", "slecht"].map((value) => (
                        <td key={value}>
                          <input
                            type="radio"
                            name={item.key}
                            value={value}
                            checked={formData[item.key] === value}
                            onChange={(e) =>
                              handleGeneralChange(item.key, e.target.value)
                            }
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>

              <table className="radio-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>{tr("yes", "Ja")}</th>
                    <th>{tr("no", "Nee")}</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      key: "goedGesorteerd",
                      label: tr(
                        "evaluationQuestionSortedCorrectly",
                        "Goed gesorteerd"
                      ),
                    },
                    {
                      key: "karrenSchoon",
                      label: tr(
                        "evaluationQuestionTrolleysClean",
                        "Karren schoon bij levering"
                      ),
                    },
                    {
                      key: "opTijd",
                      label: tr(
                        "evaluationQuestionDeliveredOnTime",
                        "Leveringen op tijd"
                      ),
                    },
                    {
                      key: "proevenAfwijkingenDuidelijk",
                      label: tr(
                        "evaluationQuestionTrialsDeviationsClear",
                        "Proeven en afwijkingen duidelijk aangegeven"
                      ),
                    },
                    {
                      key: "plantenBeschadigd",
                      label: tr(
                        "evaluationQuestionPlantsDamaged",
                        "Planten beschadigd"
                      ),
                    },
                  ].map((item) => (
                    <tr key={item.key}>
                      <td>{item.label}</td>
                      {["ja", "nee"].map((value) => (
                        <td key={value}>
                          <input
                            type="radio"
                            name={item.key}
                            value={value}
                            checked={formData[item.key] === value}
                            onChange={(e) =>
                              handleGeneralChange(item.key, e.target.value)
                            }
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {step === 4 && (
            <>
              <h2>{tr("evaluationCommunicationTitle", "Communicatie")}</h2>

              <table className="radio-table">
                <thead>
                  <tr>
                    <th>{tr("evaluationDepartmentHeader", "Afdeling")}</th>
                    <th>{tr("evaluationOptionGood", "Goed")}</th>
                    <th>{tr("evaluationOptionSufficient", "Voldoende")}</th>
                    <th>{tr("evaluationOptionInsufficient", "Onvoldoende")}</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      key: "verkoop",
                      label: tr("evaluationDepartmentSales", "Verkoop"),
                    },
                    {
                      key: "teeltManager",
                      label: tr(
                        "evaluationDepartmentCultivationManager",
                        "Teelt manager"
                      ),
                    },
                    {
                      key: "transport",
                      label: tr(
                        "evaluationDepartmentTransport",
                        "Transport afdeling"
                      ),
                    },
                  ].map((item) => (
                    <tr key={item.key}>
                      <td>{item.label}</td>
                      {["goed", "voldoende", "onvoldoende"].map((value) => (
                        <td key={value}>
                          <input
                            type="radio"
                            name={item.key}
                            value={value}
                            checked={formData[item.key] === value}
                            onChange={(e) =>
                              handleGeneralChange(item.key, e.target.value)
                            }
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {step === 5 && (
            <>
              <h2>{tr("evaluationGeneralImpressionTitle", "Algemene indruk")}</h2>

              <label>
                {tr(
                  "evaluationGeneralImpressionLabel",
                  "Algemene indruk (1 t/m 10)"
                )}
              </label>
              <input
                type="number"
                min="1"
                max="10"
                className="input-field"
                value={formData.algemeneIndruk}
                onChange={(e) =>
                  handleGeneralChange("algemeneIndruk", e.target.value)
                }
                required
                onInvalid={(e) =>
                  e.target.setCustomValidity(
                    tr(
                      "evaluationGeneralImpressionValidation",
                      "Vul een cijfer in tussen 1 en 10 om door te gaan."
                    )
                  )
                }
                onInput={(e) => e.target.setCustomValidity("")}
              />
            </>
          )}

          <div className="button-group">
            {step > 0 && (
              <button
                type="button"
                onClick={step === 1 ? prevRasStep : prevStep}
                className="secondary-button"
                disabled={saving}
              >
                {tr("previous", "Vorige")}
              </button>
            )}

            {step === 0 ? null : step < steps.length - 1 ? (
              <button
                type="button"
                onClick={step === 1 ? nextRasStep : nextStep}
                className="submit-button"
                disabled={saving}
              >
                {saving
                  ? tr("evaluationSavingButton", "Opslaan...")
                  : tr("next", "Volgende")}
              </button>
            ) : (
              <button type="submit" className="submit-button" disabled={saving}>
                {saving
                  ? tr("evaluationCompletingButton", "Afronden...")
                  : tr("evaluationCompleteButton", "Afronden")}
              </button>
            )}
          </div>
        </form>
      </div>

      <style>{`
        .evaluation-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 40px 20px;
        }

        .evaluation-box {
          width: 90%;
          max-width: 650px;
          padding: 30px;
          background: #fff;
          border: 1px solid #ddd;
          border-radius: 8px;
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
        }

        .evaluation-form {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .input-field,
        .textarea-field {
          padding: 10px;
          border: 1px solid #ccc;
          border-radius: 5px;
          font-family: inherit;
          font-size: 14px;
        }

        .textarea-field {
          min-height: 90px;
          resize: vertical;
        }

        .error-message {
          padding: 10px;
          background: #fdecea;
          border: 1px solid #f5c2c0;
          color: #8a1f11;
          border-radius: 5px;
        }

        .progress-bar {
          width: 90%;
          max-width: 650px;
          height: 8px;
          background: #eee;
          border-radius: 5px;
          margin-bottom: 20px;
          overflow: hidden;
        }

        .progress {
          height: 100%;
          background: #4caf50;
          transition: 0.3s ease;
        }

        .button-group {
          display: flex;
          justify-content: space-between;
          margin-top: 20px;
        }

        .submit-button {
          padding: 10px 20px;
          margin-left: 25px;
          background: #4caf50;
          color: #fff;
          border: none;
          border-radius: 5px;
          cursor: pointer;
        }

        .secondary-button {
          padding: 10px 20px;
          background: #ccc;
          border: none;
          border-radius: 5px;
          cursor: pointer;
        }

        .submit-button:disabled,
        .secondary-button:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }

        .radio-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 10px;
        }

        .radio-table th,
        .radio-table td {
          border-bottom: 1px solid #e5e5e5;
          padding: 10px 8px;
          text-align: center;
        }

        .radio-table th:first-child,
        .radio-table td:first-child {
          text-align: left;
          width: 50%;
          font-weight: 500;
        }

        .radio-table thead th {
          font-weight: 600;
          background: #f9f9f9;
        }

        .radio-table input[type="radio"] {
          transform: scale(1.1);
          cursor: pointer;
        }

        .landing-step,
        .transition-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 16px;
          padding: 20px 0;
        }

        .landing-step p,
        .transition-step p {
          font-size: 16px;
          color: #555;
          line-height: 1.4;
          margin: 0;
        }

        .ras-list {
          text-align: left;
          margin: 0;
          padding-left: 20px;
        }

        .ras-header {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 12px;
          background: #f9f9f9;
          border: 1px solid #e5e5e5;
          border-radius: 6px;
        }

        .ras-header span {
          font-size: 13px;
          color: #666;
        }
      `}</style>
    </div>
  );
};

export default Evaluatie;